/** The part of `navigator.locks` the claim needs. */
export type ClaimLocks = {
	request: (
		name: string,
		options: { ifAvailable: boolean },
		callback: (lock: unknown) => Promise<unknown> | undefined
	) => Promise<unknown>
}

/**
 * The Web Locks API (every current browser), read through a cast: the
 * TypeScript that compiles the tests (4.5) predates its declaration.
 */
export const browserLocks = (): ClaimLocks | undefined =>
	(navigator as Navigator & { locks?: ClaimLocks }).locks

export const claimName = (key: string): string =>
	`beelday-script-autostart:${key}`

/**
 * Runs `start` unless another tab of this browser already took the claim for
 * `key` - the trainer's lobby open in two tabs is moved to the training room
 * in both at once, and a second start would move the agenda on to item 2.
 *
 * The tab that takes the claim keeps it until it closes or reloads, which the
 * browser notices however it happens - so a tab that comes later, after the
 * start has finished, still finds it taken. A failed start lets it go, and the
 * next tab to open may try once. Without Web Locks it simply runs.
 *
 * Settles only when `start` fails; otherwise never, because holding the claim
 * is the pending promise. Callers log the failure and move on.
 */
export const claimAutoStart = (
	locks: ClaimLocks | undefined,
	key: string,
	start: () => Promise<void>
): Promise<void> => {
	if (!locks) {
		return start()
	}
	return locks
		.request(claimName(key), { ifAvailable: true }, lock =>
			lock ? start().then(() => new Promise<void>(() => undefined)) : undefined
		)
		.then(() => undefined)
}
